'use client';

import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { useIntl } from 'react-intl';
import { unmask } from '@packages/gql/src/betterMasking';
import type { EcoLabelItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/EcoLabelItemFragmentFragmentDoc';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useState } from 'react';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';
import { useEcoLabelItemTracking } from './Tracking/useEcoLabelItemTracking/useEcoLabelItemTracking';

/* GraphQL */ `
  fragment EcoLabelItemFragment on EcoLabel {
    iconUrl
    text
    serviceLayerId
    license
  }
`;

export type EcoLabelItemProps = {
  ecoLabelData: FragmentType<typeof EcoLabelItemFragmentFragmentDoc>;
  /**
   * If `ecoLabelData['text']` is `null`, this fallback is used instead.
   */
  fallbackHeadline?: string;

  mode?: 'normal' | 'productStandard';
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabelItem = ({
  fallbackHeadline,
  ecoLabelData,
  mode = 'normal',
}: EcoLabelItemProps) => {
  const { iconUrl, license, serviceLayerId, text } =
    unmask<typeof EcoLabelItemFragmentFragmentDoc>(ecoLabelData);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { trackServiceLayerClick } = useServiceLayerTracking();
  const { trackEcoLabelDisplayOverlayed } = useEcoLabelItemTracking();

  // if it's "mode" "productStandard" and iconUrl is missing we can not render the component
  // because it would have no content to display
  if (mode === 'productStandard' && !iconUrl) {
    return null;
  }

  // if iconUrl, license and text is missing we can not render the component
  // because it would have no content to display
  if (!iconUrl && !license && !text) {
    return null;
  }

  const openDialogHandler = (id: string) => {
    trackServiceLayerClick(id);
    trackEcoLabelDisplayOverlayed(text || fallbackHeadline || id);

    setIsModalOpen(true);
  };

  const closeDialogHandler = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={mode === 'normal' ? 'flex w-full items-center gap-1.5 text-left' : 'w-4'}
        onClick={serviceLayerId ? () => openDialogHandler(serviceLayerId) : undefined}
        disabled={!serviceLayerId}
      >
        {iconUrl && (
          <Image
            width={mode === 'normal' ? 52 : 32}
            height={mode === 'normal' ? 52 : 32}
            alt={text || ''}
            src={iconUrl}
            unoptimized
            className={`object-contain ${mode === 'normal' ? 'max-h-6.5 max-w-6.5' : ''}`}
          />
        )}
        {mode === 'normal' && (text || license) ? (
          <span className="flex flex-col">
            {text && <span className="text-size-body-sm underline">{text}</span>}
            {license && (
              <span className="text-size-body-xs text-color-text-darktransparent">{license}</span>
            )}
          </span>
        ) : null}
      </button>
      {serviceLayerId && (
        <Dialog isOpen={isModalOpen} onClose={closeDialogHandler} variant="bottomSheet">
          <DialogHeader onClose={closeDialogHandler}>
            {mode === 'normal'
              ? text || fallbackHeadline
              : formatMessage({
                  id: 'ecoLabel.productStandard.dialog.title',
                  defaultMessage: 'Productstandard',
                })}
          </DialogHeader>
          <DialogContent>
            <ServiceLayer serviceLayerId={serviceLayerId} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
